import { useId, useState, type FormEvent } from 'react';
import { Alert } from '../../components/Alert';
import { useT } from '../../i18n';
import { describeError } from '../../lib/errors';

export interface FeedbackValues {
  rating: number;
  text?: string;
  opt_out?: boolean;
}

export const MAX_TEXT = 2000;

/**
 * Five large rating buttons (aria-pressed), an optional text and — on the public page — «Більше не надсилати…».
 * `onSubmit` throws on failure; the caller decides what a 409/410 means.
 */
export function FeedbackForm({ onSubmit, showOptOut = false }: { onSubmit: (v: FeedbackValues) => Promise<void>; showOptOut?: boolean }) {
  const t = useT();
  const ids = useId();
  const [rating, setRating] = useState<number | null>(null);
  const [text, setText] = useState('');
  const [optOut, setOptOut] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [needRating, setNeedRating] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!rating) {
      setNeedRating(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = text.trim();
      await onSubmit({ rating, ...(body ? { text: body.slice(0, MAX_TEXT) } : {}), ...(showOptOut ? { opt_out: optOut } : {}) });
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} noValidate className="mt-6 flex flex-col gap-8">
      <fieldset>
        <legend className="text-[15px] text-txt">{t('feedback.rating')}</legend>
        <div role="group" aria-describedby={`${ids}-scale`} className="mt-3 grid grid-cols-5 gap-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              aria-pressed={rating === n}
              aria-label={t('feedback.rate', { n })}
              onClick={() => {
                setRating(n);
                setNeedRating(false);
              }}
              className={`min-h-[56px] min-w-[48px] border text-[20px] transition-colors ${rating === n ? 'border-fg bg-fg text-bg' : 'border-line hover:border-fg'}`}
            >
              {n}
            </button>
          ))}
        </div>
        <p id={`${ids}-scale`} className="mt-2 text-[13px] text-lab">
          {t('feedback.scale')}
        </p>
        {needRating ? (
          <div className="mt-3">
            <Alert kind="error">{t('feedback.pickRating')}</Alert>
          </div>
        ) : null}
      </fieldset>

      <div>
        <label htmlFor={`${ids}-text`} className="text-[15px] text-txt">
          {t('feedback.text')}
        </label>
        <textarea
          id={`${ids}-text`}
          value={text}
          maxLength={MAX_TEXT}
          rows={4}
          onChange={(e) => setText(e.target.value)}
          className="field-input mt-2 resize-y text-[16px] leading-relaxed"
        />
      </div>

      {showOptOut ? (
        <label className="flex min-h-tap cursor-pointer items-start gap-3 text-[15px] text-txt">
          <input type="checkbox" checked={optOut} onChange={(e) => setOptOut(e.target.checked)} className="mt-1 h-5 w-5 flex-none" />
          <span>{t('feedback.optOut')}</span>
        </label>
      ) : null}

      <div aria-live="polite">{error ? <Alert kind="error">{t(describeError(error))}</Alert> : null}</div>
      <button type="submit" className="btn-solid w-full sm:w-auto" disabled={busy}>
        {busy ? t('feedback.sending') : t('feedback.submit')}
      </button>
    </form>
  );
}
